import { Platform } from 'react-native';
import * as Linking from 'expo-linking';
import { getAPIBaseURL } from './api-url';
import { getAppScheme } from './app-scheme';

/**
 * App domain used for universal links in production.
 * Must match the associatedDomains / assetlinks configuration.
 */
const APP_DOMAIN = 'app.tabbio.com';
export const UAE_PASS_CALLBACK_PATH = '/auth/uaepass/callback';
export const UAE_PASS_LEGACY_CALLBACK_PATH = '/uae-pass/callback';

/**
 * Returns the base URL for email redirect links (password reset, verification, etc.).
 *
 * Email links MUST always be HTTP(S): never a custom scheme like `tabbio://`.
 * The user may open the email on any device (desktop browser, phone browser, etc.).
 *
 * - **Web**: uses `window.location.origin` (e.g. `http://localhost:8081`)
 * - **Native dev**: uses the Expo dev web server origin so the link works in any browser
 * - **Native prod**: uses `https://app.tabbio.com` (universal links open the app)
 */
function getEmailRedirectBase(): string {
  if (Platform.OS === 'web') {
    return typeof window !== 'undefined' ? window.location.origin : `https://${APP_DOMAIN}`;
  }

  // Native: always use an HTTP URL so links work in any browser.
  // In production, universal links will intercept and open the app.
  if (__DEV__) {
    // Dev: point to the Expo web server so the reset page loads in a browser.
    // Expo web dev server runs alongside the native dev server.
    // Derive the host from the API URL (same machine, different port).
    const apiBase = getAPIBaseURL();
    try {
      const url = new URL(apiBase);
      // Expo web dev server is typically on port 8081
      url.port = '8081';
      return url.origin;
    } catch {
      return 'http://localhost:8081';
    }
  }

  return `https://${APP_DOMAIN}`;
}

/**
 * Builds a full redirect URL for email links (password reset, email verification, etc.).
 *
 * These URLs are embedded in emails and opened in the user's browser.
 * They always use HTTP(S): never custom schemes.
 *
 * @example
 * buildRedirectURL('/(auth)/reset-password')
 * // Dev (any platform): "http://localhost:8081/(auth)/reset-password"
 * // Prod:               "https://app.tabbio.com/(auth)/reset-password"
 */
export function buildRedirectURL(path: string): string {
  const base = getEmailRedirectBase();

  const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;

  return `${cleanBase}${cleanPath}`;
}

export function buildSubscriptionReturnUrl(
  params?: {
    status?: 'success' | 'canceled' | 'failed';
    planSet?: 'individual' | 'employer';
    tier?: 'plus' | 'pro';
    billingInterval?: 'monthly' | 'yearly';
    companyId?: string | null;
  },
) {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const url = new URL('/subscription/return', window.location.origin);
    if (params?.status) url.searchParams.set('status', params.status);
    if (params?.planSet) url.searchParams.set('planSet', params.planSet);
    if (params?.tier) url.searchParams.set('tier', params.tier);
    if (params?.billingInterval) url.searchParams.set('billingInterval', params.billingInterval);
    if (params?.companyId) url.searchParams.set('companyId', params.companyId);
    return url.toString();
  }

  const url = Linking.createURL('/subscription/return', { scheme: getAppScheme() });
  const parsed = new URL(url);
  if (params?.status) parsed.searchParams.set('status', params.status);
  if (params?.planSet) parsed.searchParams.set('planSet', params.planSet);
   if (params?.tier) parsed.searchParams.set('tier', params.tier);
  if (params?.billingInterval) parsed.searchParams.set('billingInterval', params.billingInterval);
  if (params?.companyId) parsed.searchParams.set('companyId', params.companyId);
  return parsed.toString();
}

export function buildUaePassAppCallbackUrl() {
  return Linking.createURL(UAE_PASS_CALLBACK_PATH, { scheme: getAppScheme() });
}

export function buildLegacyUaePassAppCallbackUrl() {
  return Linking.createURL(UAE_PASS_LEGACY_CALLBACK_PATH, { scheme: getAppScheme() });
}

/**
 * The domain used for universal links. Exported for use in
 * app config and well-known file generation.
 */
export const UNIVERSAL_LINK_DOMAIN = APP_DOMAIN;
